import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import { Close, DeleteOutline, Edit, Search, Colorize } from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import ConfirmationModal from "../ConfirmationModal";
import "./kanbanLaneManager.css";

const PALETTE = [
  "#a6ee75", "#46873a", "#ffdd33", "#ff8c20",
  "#f42330", "#a20fd9", "#ed239d", "#1275e8",
  "#1d3298", "#6022db", "#8545c7", "#71869d",
  "#93644b", "#878787", "#12a89f", "#e4e4e4"
];

const DEFAULT_COLOR = "#46873a";
const emptyDraft = () => ({ name: "", color: DEFAULT_COLOR });

const KanbanLaneManager = ({ open, onClose, onChange, laneCounts = {} }) => {
  const { user, socket } = useContext(AuthContext);
  const [lanes, setLanes] = useState([]);
  const [query, setQuery] = useState("");
  const [draft, setDraft] = useState(emptyDraft);
  const [editingId, setEditingId] = useState(null);
  const [editingTag, setEditingTag] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [deletingLane, setDeletingLane] = useState(null);
  const deletingLaneRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadLanes = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      // Esta rota já retorna todas as lanes da empresa, sem paginação.
      // Evita depender da contagem de joins de tickets do /tags/.
      const { data } = await api.get("/tag/kanban/");
      setLanes(data.lista || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    loadLanes();
  }, [open, loadLanes]);

  useEffect(() => {
    if (!open || !socket || !user) return;
    const eventName = `company${user.companyId}-tag`;
    const handleTagEvent = (event) => {
      if (["create", "update", "delete"].includes(event.action)) {
        loadLanes();
      }
    };
    socket.on(eventName, handleTagEvent);
    return () => socket.off(eventName, handleTagEvent);
  }, [open, socket, user, loadLanes]);

  const shown = useMemo(() => {
    const term = query.trim().toLocaleLowerCase("pt-BR");
    return lanes.filter(lane => String(lane.name || "").toLocaleLowerCase("pt-BR").includes(term));
  }, [lanes, query]);

  const resetDraft = () => {
    setDraft(emptyDraft());
    setEditingId(null);
    setEditingTag(null);
  };

  const editLane = async lane => {
    setEditLoading(true);
    try {
      // A lista só traz nome/cor. Buscar o registro inteiro é essencial:
      // UpdateService preserva timeLane/nextLaneId/greetingMessageLane/
      // rollbackLaneId apenas se enviados, evitando apagar automações.
      const { data } = await api.get(`/tags/${lane.id}`);
      setEditingTag(data);
      setEditingId(lane.id);
      setDraft({ name: data.name || "", color: data.color || DEFAULT_COLOR });
    } catch (err) {
      toastError(err);
    } finally {
      setEditLoading(false);
    }
  };

  const saveLane = async event => {
    event.preventDefault();
    if (saving || editLoading || (editingId != null && !editingTag)) return;
    const name = draft.name.trim();
    if (name.length < 3) {
      toast.error("O nome da lane deve ter pelo menos 3 caracteres.");
      return;
    }
    setSaving(true);
    try {
      // Não tocar nas configurações automáticas da lane ao editar só nome/cor.
      const automationFields = editingTag ? {
        timeLane: editingTag.timeLane,
        nextLaneId: editingTag.nextLaneId,
        greetingMessageLane: editingTag.greetingMessageLane,
        rollbackLaneId: editingTag.rollbackLaneId
      } : {};
      const payload = { ...automationFields, name, color: draft.color, kanban: 1, userId: user.id };
      if (editingId != null) {
        await api.put(`/tags/${editingId}`, payload);
      } else {
        await api.post("/tags", payload);
      }
      toast.success(editingId != null ? "Lane atualizada com sucesso." : "Lane criada com sucesso.");
      resetDraft();
      await loadLanes();
      if (onChange) onChange();
    } catch (err) {
      // Preserva o nome e a cor também se houver falha de rede/validação.
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const removeLane = async () => {
    // ConfirmationModal fecha antes de chamar onConfirm. Manter a lane
    // num ref impede que o ID seja perdido nesse intervalo.
    const lane = deletingLaneRef.current;
    if (!lane || deleting) return;
    setDeleting(true);
    try {
      await api.delete(`/tags/${lane.id}`);
      toast.success("Lane excluída com sucesso.");
      if (editingId === lane.id) resetDraft();
      deletingLaneRef.current = null;
      setDeletingLane(null);
      await loadLanes();
      if (onChange) onChange();
    } catch (err) {
      toastError(err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={saving ? undefined : onClose}
        maxWidth="lg"
        fullWidth
        scroll="paper"
        aria-labelledby="kanban-lanes-manager-title"
        PaperProps={{ className: "ag-lanes-dialog" }}
      >
        <DialogTitle disableTypography className="ag-lanes-title" id="kanban-lanes-manager-title">
          <div>
            <Typography variant="h6"><strong>Gerenciar Lanes</strong></Typography>
            <Typography variant="body2" color="textSecondary">
              Crie, edite ou remova as colunas do seu Kanban
            </Typography>
          </div>
          <IconButton aria-label="Fechar gestão de lanes" onClick={onClose} disabled={saving} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers className="ag-lanes-content">
          <section className="ag-lanes-form-panel" aria-label="Cadastro de lane">
            <div className="ag-lanes-panel-heading">
              {editingId != null ? "Editar Lane" : "Nova Lane"}
            </div>
            <form onSubmit={saveLane} className="ag-lanes-form">
              <label htmlFor="ag-lane-name">Nome da lane</label>
              <TextField
                id="ag-lane-name"
                name="lane-name"
                value={draft.name}
                onChange={event => {
                  // React 16 reutiliza SyntheticEvent: ler o valor antes de enfileirar setState.
                  const name = event.target.value;
                  setDraft(prev => ({ ...prev, name }));
                }}
                placeholder="Ex.: Em análise"
                fullWidth
                variant="outlined"
                size="small"
                inputProps={{ maxLength: 120, minLength: 3 }}
                autoComplete="off"
              />
              <label>Cor da lane</label>
              <div className="ag-lanes-palette" role="group" aria-label="Escolher cor da lane">
                {PALETTE.map(color => (
                  <Tooltip title={color} key={color}>
                    <button
                      type="button"
                      className={`ag-lanes-swatch ${draft.color.toLowerCase() === color.toLowerCase() ? "is-selected" : ""}`}
                      style={{ backgroundColor: color }}
                      aria-label={`Selecionar cor ${color}`}
                      aria-pressed={draft.color.toLowerCase() === color.toLowerCase()}
                      onClick={() => setDraft(prev => ({ ...prev, color }))}
                    />
                  </Tooltip>
                ))}
                <label className="ag-lanes-custom-color" title="Selecionar outra cor">
                  <Colorize fontSize="small" />
                  <input
                    type="color"
                    aria-label="Escolher outra cor"
                    value={/^#[0-9a-f]{6}$/i.test(draft.color) ? draft.color : DEFAULT_COLOR}
                    onChange={event => {
                      const color = event.target.value;
                      setDraft(prev => ({ ...prev, color }));
                    }}
                  />
                </label>
              </div>
              <div className="ag-lanes-preview">
                <small>Prévia</small>
                <span style={{ backgroundColor: draft.color }}>
                  {draft.name.trim() || "Nome da lane"}
                </span>
              </div>
              <div className="ag-lanes-form-actions">
                <Button type="button" variant="outlined" onClick={resetDraft} disabled={saving || editLoading}>
                  Cancelar
                </Button>
                <Button color="primary" variant="contained" type="submit" disabled={saving || editLoading}>
                  {saving || editLoading ? <CircularProgress size={18} color="inherit" /> : editingId != null ? "Salvar alterações" : "Salvar Lane"}
                </Button>
              </div>
            </form>
          </section>
          <section className="ag-lanes-list-panel" aria-label="Lanes cadastradas">
            <div className="ag-lanes-list-heading">
              <strong>Lanes cadastradas ({lanes.length + 1})</strong>
              <TextField
                size="small"
                variant="outlined"
                placeholder="Pesquisar lanes..."
                value={query}
                onChange={event => setQuery(event.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                inputProps={{ "aria-label": "Pesquisar lanes cadastradas" }}
              />
            </div>
            <div className="ag-lanes-table-header" aria-hidden="true">
              <span>Cor</span><span>Nome</span><span>Tickets</span><span>Ações</span>
            </div>
            <div className="ag-lanes-scroll" role="list">
              {(!query || "em aberto".includes(query.trim().toLocaleLowerCase("pt-BR"))) && (
                <div className="ag-lanes-row" role="listitem">
                  <span className="ag-lanes-dot" style={{ backgroundColor: "#ddd" }} />
                  <span title="Em aberto">Em aberto <small>(padrão)</small></span>
                  <span>{laneCounts.lane0 || 0}</span>
                  <span className="ag-lanes-row-actions" title="Esta etapa padrão não pode ser alterada">—</span>
                </div>
              )}
              {shown.map(lane => (
                <div className="ag-lanes-row" role="listitem" key={lane.id}>
                  <span className="ag-lanes-dot" style={{ backgroundColor: lane.color }} />
                  <span className="ag-lanes-row-name" title={lane.name}>{lane.name}</span>
                  <span>{laneCounts[String(lane.id)] || 0}</span>
                  <span className="ag-lanes-row-actions">
                    <Tooltip title="Editar lane">
                      <IconButton size="small" aria-label={`Editar ${lane.name}`} onClick={() => editLane(lane)}><Edit fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir lane">
                      <IconButton size="small" aria-label={`Excluir ${lane.name}`} onClick={() => { deletingLaneRef.current = lane; setDeletingLane(lane); }}><DeleteOutline fontSize="small" /></IconButton>
                    </Tooltip>
                  </span>
                </div>
              ))}
              {shown.length === 0 && query.trim() && !"em aberto".includes(query.trim().toLocaleLowerCase("pt-BR")) && (
                <div className="ag-lanes-empty">Nenhuma lane encontrada.</div>
              )}
            </div>
            {loading && <div className="ag-lanes-loading"><CircularProgress size={18} /> Atualizando lanes...</div>}
            <p className="ag-lanes-help">A lista possui rolagem interna para facilitar a gestão de 20 ou mais lanes.</p>
          </section>
        </DialogContent>
        <DialogActions className="ag-lanes-bottom">
          <Button variant="outlined" onClick={onClose} disabled={saving}>Fechar</Button>
        </DialogActions>
      </Dialog>
      <ConfirmationModal
        title="Excluir lane"
        open={Boolean(deletingLane)}
        onClose={() => setDeletingLane(null)}
        onConfirm={removeLane}
      >
        {`Tem certeza que deseja excluir a lane “${deletingLane?.name || ""}”?`}
      </ConfirmationModal>
    </>
  );
};

export default KanbanLaneManager;
